import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import BoxedContent from 'ui/molecules/boxed-content';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import { AdminCampaign, DistributionPlatformTypeEnum } from 'api/models';
import LoadingRing from 'ui/atoms/loading-ring';
import Translate from 'ui/atoms/translate';
import useClipboard from 'ui/hooks/use-clipboard';
import { handleError } from 'ui/helper/error-handling';
import useClearAfterTimeout from 'ui/hooks/use-clear-after-timeout';
import useTranslate from 'ui/hooks/use-translate';
import ProductCampaignExtendedDetailsCard from 'apps/issuer/pages/product-details/campaigns/campaign-details/product-campaign-card';
import NoInvestmentDocumentsModal from './no-investment-documents-modal';
import { compact } from 'lodash';
import Hint from 'src/ui/atoms/hint';
import { hasDistributionViewDataPermission } from 'src/apps/issuer/helpers/distributionPlatformPermissions';

type BoxedIssuerCampaignProps = AdminCampaign & {
  issuersCanManage?: boolean;
};

const BoxedIssuerCampaign: FunctionComponent<BoxedIssuerCampaignProps> = (props) => {
  const { issuersCanManage, ...campaignProps } = props;
  const [campaign, setCampaign] = useState<AdminCampaign>(campaignProps);
  const [noInvestmentDocumentsModalOpen, setNoInvestmentDocumentsModalOpen] = useState(false);

  const {
    id,
    allocation,
    allocationAvailable,
    campaignLink,
    canActivate,
    distributionPlatformDetails,
    minNumberOfTokens,
    maxNumberOfTokens,
    pricePerToken,
    ruleType,
    country,
    active,
    token,
    name,
    signedPercentage,
    createdAt,
    distributionInformation,
  } = campaign;

  const { withApi, makeAuthenticatedApi, loading, error } = useApiCall();
  const { copy, copied } = useClipboard();

  const { getRemainingError } = handleError({
    error: useClearAfterTimeout(error),
    translate: useTranslate(),
  });

  const answerErrors = getRemainingError();

  useEffect(() => {
    const { issuersCanManage, ...campaignProps } = props;
    setCampaign(campaignProps);
  }, [props]);

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const activate = useCallback(() => {
    withApi(async () => {
      if (id) {
        await api.adminCampaignsActivationCreate({
          id,
          campaignActivationRequest: { active: !active },
        });
        const campaignNew = await api.adminCampaignsRetrieve({ id });
        setCampaign(campaignNew);
      }
    });
  }, [withApi, api, id, active]);

  const copyButtonAction = useCallback(() => {
    return !distributionPlatformDetails.usesEffecta || distributionInformation?.investmentDocuments?.length
      ? copy(campaignLink)
      : setNoInvestmentDocumentsModalOpen(true);
  }, [JSON.stringify(distributionInformation?.investmentDocuments), campaignLink]);

  return (
    <>
      <BoxedContent
        releaseSpaceWhenTitleIsEmpty={true}
        title={<Translate name="common.generalInformation" />}
        actionButtons={compact([
          issuersCanManage &&
            active && {
              name: 'deactivateCampaign',
              content: <Translate name="issuerCampaigns.deactivateCampaign" />,
              error: answerErrors,
              onClick: activate,
            },
          issuersCanManage &&
            !active && {
              name: 'reactivateCampaign',
              content: <Translate name="issuerCampaigns.reactivateCampaign" />,
              variant: 'primary',
              error: answerErrors,
              disabled:
                (!canActivate && allocationAvailable > 0) ||
                (token.ewpg && (token.publicationValid === false || !token.issuanceDocument?.publicationDate)),
              onClick: activate,
            },
          hasDistributionViewDataPermission([distributionPlatformDetails]) &&
            distributionPlatformDetails.platformType === DistributionPlatformTypeEnum.FRONTEND && {
              name: 'copyLinkToClipboard',
              content: <Translate name={`copy.link.${!copied ? 'copy' : 'copied'}`} />,
              variant: 'primary',
              disabled: copied,
              onClick: copyButtonAction,
            },
        ])}
        extraInfo={
          !distributionInformation &&
          distributionPlatformDetails.usesEffecta && (
            <Hint variant="warning">
              <Translate name="issuerCampaigns.noDistributionInformation" />
            </Hint>
          )
        }
      >
        {loading && <LoadingRing />}
        {!loading && (
          <ProductCampaignExtendedDetailsCard
            id={id}
            allocation={allocation}
            allocationAvailable={allocationAvailable}
            minNumberOfTokens={minNumberOfTokens}
            maxNumberOfTokens={maxNumberOfTokens}
            distributionPlatform={distributionPlatformDetails.displayName}
            pricePerToken={pricePerToken}
            ruleType={ruleType as any}
            country={country as any}
            active={active}
            token={token}
            name={name}
            createdAt={createdAt}
            signedPercentage={signedPercentage}
          />
        )}
      </BoxedContent>
      {noInvestmentDocumentsModalOpen && (
        <NoInvestmentDocumentsModal
          campaignLink={campaignLink}
          onClose={() => setNoInvestmentDocumentsModalOpen(false)}
        />
      )}
    </>
  );
};

export default BoxedIssuerCampaign;
